import * as Yup from "yup";
import type { TestContext } from "yup";
import { getNetCountMode, isCounterOccurrencePrimitive } from "./TargetBuilderPanel.types";
import type { TargetFormValues } from "./TargetBuilderPanel.types";

// Helper to get the root form values from the Yup test context. This is necessary because Yup's `this.parent` only gives you
// the immediate parent object, and we need access to the entire form state for certain validations.
const getRootFormValues = (ctx: TestContext): Partial<TargetFormValues> | undefined =>
  ctx.from?.[ctx.from.length - 1]?.value;

const numberRequired = (msg = "Required") =>
  Yup.number().typeError(msg).required(msg).min(1, "Must be at least 1");

const thresholdRequired = () =>
  Yup.number()
    .typeError("Required")
    .required("Required")
    .test("l1-threshold-range", "Must be >= 0", function (value) {
      if (value == null) return false;
      const root = getRootFormValues(this);
      const l1 = root?.l1_dimension;
      const l3 = root?.l3_context;
      const isCounterOccurrence = isCounterOccurrencePrimitive(
        (l1 as Parameters<typeof isCounterOccurrencePrimitive>[0]) ?? "",
        (l3 as Parameters<typeof isCounterOccurrencePrimitive>[1]) ?? "",
        root?.teaching_procedure,
        root?.target_type as Parameters<typeof isCounterOccurrencePrimitive>[3]
      );
      if (isCounterOccurrence) {
        if (!Number.isInteger(value)) {
          return this.createError({ message: "Must be a whole number" });
        }
        return value >= 1 ? true : this.createError({ message: "Must be at least 1" });
      }
      if (l1 === "frequency") {
        return value >= 0 && value <= 100
          ? true
          : this.createError({ message: "Must be between 0 and 100" });
      }
      if (l1 === "magnitude") {
        if (!Number.isInteger(value)) {
          return this.createError({ message: "Must be a whole number" });
        }
        return value >= 0 && value <= 5
          ? true
          : this.createError({ message: "Must be between 0 and 5" });
      }
      return value >= 0;
    });

const thresholdUnitSchema = () =>
  Yup.string().test("l1-threshold-unit", "Invalid threshold unit", function (value) {
    const root = getRootFormValues(this);
    const l1 = root?.l1_dimension;
    const l3 = root?.l3_context;
    const isCounterOccurrence = isCounterOccurrencePrimitive(
      (l1 as Parameters<typeof isCounterOccurrencePrimitive>[0]) ?? "",
      (l3 as Parameters<typeof isCounterOccurrencePrimitive>[1]) ?? "",
      root?.teaching_procedure,
      root?.target_type as Parameters<typeof isCounterOccurrencePrimitive>[3]
    );

    if (l1 === "duration" || l1 === "latency") {
      return value === "seconds" || value === "minutes"
        ? true
        : this.createError({ message: "Select seconds or minutes" });
    }
    if (l1 === "frequency") {
      if (isCounterOccurrence) {
        return !value || value === "occurrences"
          ? true
          : this.createError({ message: "Counter-occurrence thresholds must use occurrences" });
      }
      return !value || value === "percent"
        ? true
        : this.createError({ message: "Frequency thresholds must use percent" });
    }
    if (l1 === "magnitude") {
      return !value || value === "rating"
        ? true
        : this.createError({ message: "Magnitude thresholds must use rating" });
    }

    return true;
  });

const progressCriteriaShape = () =>
  Yup.object({
    consecutive_points: numberRequired(),
    percent: thresholdRequired(),
    threshold_unit: thresholdUnitSchema(),
    direction: Yup.string().oneOf(["above", "below"]).required("Required direction"),
    notes: Yup.string(),
  });

export interface TargetValidationSchemaOptions {
  /**
   * Whether the client has any behavior-reduction goals available to link as
   * the replaced goal. When false the dropdown renders an empty-state message
   * instead of options, so `replacement_for_goal_id` cannot be satisfied and
   * stops being required — the payload sends `null` for the link in that case.
   */
  hasReplacementGoalOptions: boolean;
  goalBehaviorSubject?: "client" | "caregiver";
}

export const buildTargetValidationSchema = ({
  hasReplacementGoalOptions,
  goalBehaviorSubject = "client",
}: TargetValidationSchemaOptions) =>
  Yup.object({
    title: Yup.string().trim().required("Target name is required"),
    target_type: Yup.string().required("Required"),
    phase: Yup.string().required("Required"),
    status: Yup.string().required("Required"),
    primary_implementer: Yup.string().required("Required"),
    implementation_setting: Yup.string()
      .oneOf(["home", "community", "school"], "Invalid implementation setting")
      .required("Required"),

    baseline_progression_criteria: Yup.object({
      to_intervention: progressCriteriaShape(),
      to_generalization: progressCriteriaShape(),
    }),

    // FCR conditional
    is_replacement_behavior: Yup.boolean(),
    replacement_for_goal_id: Yup.mixed().when("is_replacement_behavior", {
      is: (v: boolean) => v && hasReplacementGoalOptions,
      then: () => Yup.number().typeError("Required").required("Select the goal this replaces"),
      otherwise: () => Yup.mixed().nullable(),
    }),
    eo_conditions: Yup.string().when("is_replacement_behavior", {
      is: true,
      then: (s) => s.trim().required("Required"),
    }),
    prompting_strategy_under_eo: Yup.string().when("is_replacement_behavior", {
      is: true,
      then: (s) => s.trim().required("Required"),
    }),
    competing_response_considerations: Yup.string().when("is_replacement_behavior", {
      is: true,
      then: (s) => s.trim().required("Required"),
    }),

    // L-Model
    l1_dimension: Yup.string().required("Select a behavioral dimension"),
    l2_method: Yup.string().required("Select a recording method"),
    l3_context: Yup.string().required("Select an instructional context"),

    // Criteria — all required per the redesigned Section 3 spec
    mastery_criteria: progressCriteriaShape(),
    regression_criteria: progressCriteriaShape(),
    generalization_criteria: progressCriteriaShape(),
    maintenance_plan: Yup.object({
      first_month_every_n_sessions: numberRequired(),
      next_three_months_every_n_sessions: numberRequired(),
      notes: Yup.string(),
    }),

    // Teaching procedure — validate count_prompted_as_correct flag constraints.
    teaching_procedure: Yup.object()
      .nullable()
      .optional()
      .test("br-required-fields", "Missing required behavior reduction fields", function (value) {
        const root = getRootFormValues(this);
        const targetType = root?.target_type;
        if (targetType !== "behavior-reduction") return true;
        if (!value || typeof value !== "object") {
          return this.createError({ message: "Teaching procedure is required" });
        }

        const obj = value as Record<string, unknown>;
        const requiredStringFields: Array<[string, string]> = [
          ["date_of_implementation", "Date of implementation is required"],
          ["operational_definition", "Operational definition is required"],
          ["assessment_used", "Assessment used is required"],
          ["common_antecedents", "Common antecedents is required"],
        ];

        for (const [field, message] of requiredStringFields) {
          if (typeof obj[field] !== "string" || obj[field].trim().length === 0) {
            return this.createError({ message, path: `${this.path}.${field}` });
          }
        }

        if (typeof obj.function !== "string" || obj.function.trim().length === 0) {
          return this.createError({
            message: "Function is required",
            path: `${this.path}.function`,
          });
        }

        return true;
      })
      .test("caregiver-required-fields", "Missing required caregiver fields", function (value) {
        if (goalBehaviorSubject !== "caregiver") return true;
        if (!value || typeof value !== "object") {
          return this.createError({ message: "Teaching procedure is required" });
        }
        const obj = value as Record<string, unknown>;
        if (
          typeof obj.error_correction_procedure !== "string" ||
          obj.error_correction_procedure.trim().length === 0
        ) {
          return this.createError({
            message: "Error correction procedure is required",
            path: `${this.path}.error_correction_procedure`,
          });
        }
        return true;
      })
      .test(
        "behavior-reduction-no-prompt-hierarchy",
        "Behavior-reduction targets cannot use prompt-hierarchy scoring",
        function (value) {
          const root = getRootFormValues(this);
          if (root?.target_type !== "behavior-reduction") return true;
          if (!value || typeof value !== "object") return true;

          const proc = value as Record<string, unknown>;
          const phRaw = proc.prompt_hierarchy;
          const levels =
            phRaw && typeof phRaw === "object" && !Array.isArray(phRaw)
              ? (phRaw as Record<string, unknown>).levels
              : undefined;
          if (Array.isArray(levels) && levels.length > 0) {
            return this.createError({
              message: "Behavior-reduction targets cannot use prompt hierarchy",
              path: `${this.path}.prompt_hierarchy.levels`,
            });
          }

          if (proc.type === "task-analysis" && proc.step_scoring === "prompt-hierarchy") {
            return this.createError({
              message: "Behavior-reduction targets must use binary step scoring",
              path: `${this.path}.step_scoring`,
            });
          }

          return true;
        }
      )
      .test("mastery-prompt-level-required", "Select a mastery prompt level", function (value) {
        const root = getRootFormValues(this);
        if (!isPromptHierarchyVisible(root, goalBehaviorSubject)) return true;

        // Anchored at the teaching_procedure level (not prompt_hierarchy)
        // because Yup skips tests on undefined values — a procedure missing
        // its prompt_hierarchy entirely must still fail when the field is visible.
        const ph = (value as Record<string, unknown> | null | undefined)?.prompt_hierarchy;
        const mpl =
          ph && typeof ph === "object" && !Array.isArray(ph)
            ? (ph as Record<string, unknown>).mastery_prompt_level
            : undefined;
        if (typeof mpl !== "string" || mpl.trim().length === 0) {
          return this.createError({
            message: "Select a mastery prompt level",
            path: `${this.path}.prompt_hierarchy.mastery_prompt_level`,
          });
        }
        return true;
      })
      .test("net-prompt-levels-required", "Select at least one prompt level", function (value) {
        // NET in prompt-hierarchy mode must have levels configured — an empty
        // hierarchy would silently fall back to occurrence/C-I on the backend
        // (a non-empty `prompt_hierarchy` array is what selects the PH
        // primitive there).
        const root = getRootFormValues(this);
        if (root?.l3_context !== "net") return true;
        const l1 = root?.l1_dimension;
        if (l1 !== "frequency" && l1 !== "latency") return true;
        if (!isPromptHierarchyVisible(root, goalBehaviorSubject)) return true;
        if (getNetCountMode(root?.teaching_procedure, l1, root?.target_type) !== "prompt-hierarchy")
          return true;

        const ph = (value as Record<string, unknown> | null | undefined)?.prompt_hierarchy;
        const levels =
          ph && typeof ph === "object" && !Array.isArray(ph)
            ? (ph as Record<string, unknown>).levels
            : undefined;
        if (!Array.isArray(levels) || levels.length === 0) {
          return this.createError({
            message: "Select at least one prompt level",
            path: `${this.path}.prompt_hierarchy.levels`,
          });
        }
        return true;
      })
      .shape({
        prompt_hierarchy: Yup.object().nullable().optional().shape({
          count_prompted_as_correct: Yup.boolean(),
          mastery_prompt_level: Yup.string(),
        }),
      }),
  });

/**
 * Mirrors the Target Builder's prompt-hierarchy hide rules so
 * validation requires Mastery Prompt Level exactly when the field is shown:
 *   - hidden when l2_method === "anchored-rating-scale"
 *   - hidden when the goal's behavior subject is caregiver
 *   - hidden when target_type === "behavior-reduction"
 *   - hidden when l3_context has no prompt hierarchy (free-operant, or unset)
 *   - for task-analysis, only visible when step_scoring === "prompt-hierarchy"
 *   - for net with a count-type selector (frequency/latency), only visible
 *     when the selected count mode is "prompt-hierarchy"
 */
const isPromptHierarchyVisible = (
  root: Partial<TargetFormValues> | undefined,
  goalBehaviorSubject: "client" | "caregiver"
): boolean => {
  if (!root) return false;

  const { l1_dimension, l2_method, target_type, l3_context, teaching_procedure } = root;

  const hidden =
    l2_method === "anchored-rating-scale" ||
    goalBehaviorSubject === "caregiver" ||
    target_type === "behavior-reduction";
  if (hidden) return false;

  if (l3_context !== "discrete-trial" && l3_context !== "net" && l3_context !== "task-analysis") {
    return false;
  }

  if (l3_context === "task-analysis") {
    return teaching_procedure?.type === "task-analysis"
      ? teaching_procedure.step_scoring === "prompt-hierarchy"
      : false;
  }

  if (l3_context === "net" && (l1_dimension === "frequency" || l1_dimension === "latency")) {
    return getNetCountMode(teaching_procedure, l1_dimension) === "prompt-hierarchy";
  }

  return true;
};
